/**
 * ValidationPreviewResult Component
 *
 * Result of the AI validation dry-run — the findings it would add.
 */
import { Badge, Button, Loader } from '@insio/ui-toolkit';
import { useFeatureTranslation } from '@/hooks/useFeatureTranslation';

/** What a validation dry-run reports — the invoice and requisition previews share this shape. */
export interface ValidationPreviewOutcome {
  issues: ReadonlyArray<{ severity: string; code: string; path: string; message: string }>;
  confidenceScore?: number | null;
}

export interface ValidationPreviewResultProps {
  isRunning: boolean;
  hasError: boolean;
  result?: ValidationPreviewOutcome | null;
  /** Closes the panel until the next dry-run. */
  onDismiss: () => void;
  /** Feature translation prefix of the `preview.*` texts; the invoice ones by default. */
  translationPrefix?: string;
}

export function ValidationPreviewResult({
  isRunning,
  hasError,
  result,
  onDismiss,
  translationPrefix = 'systemSettings.automation.validation.preview'
}: ValidationPreviewResultProps) {
  const t = useFeatureTranslation(translationPrefix);

  if (!isRunning && !hasError && !result) {
    return null;
  }

  return (
    <div
      data-testid='validation-preview-section'
      className='rounded-xl border border-border-base bg-background-base p-md'
    >
      <div className='flex items-start justify-between gap-sm'>
        <h2 className='text-base font-semibold text-foreground-base'>{t('resultTitle')}</h2>
        <Button
          variant='ghost'
          size='sm'
          icon='xmark'
          onClick={onDismiss}
          aria-label={t('close')}
          testId='validation-preview-dismiss'
        />
      </div>

      {isRunning && <Loader.Section />}
      {hasError && !isRunning && <p className='text-sm text-error-text mt-sm'>{t('error')}</p>}
      {result && !isRunning && !hasError && (
        <div className='mt-sm space-y-sm' data-testid='validation-preview-result'>
          <div className='flex items-center gap-sm flex-wrap'>
            <Badge color={result.issues.length === 0 ? 'success' : 'warning'} size='md'>
              {result.issues.length === 0
                ? t('noFindings')
                : t('findingsCount', { count: result.issues.length })}
            </Badge>
            {result.confidenceScore != null && (
              <span className='text-sm text-foreground-muted'>
                {t('confidence', { value: Math.round(result.confidenceScore) })}
              </span>
            )}
          </div>
          {result.issues.map((issue, index) => (
            <div key={`${issue.code}-${issue.path}-${index}`} className='flex items-start gap-sm'>
              <Badge color={issue.severity === 'error' ? 'error' : 'warning'} size='sm'>
                {issue.severity === 'error' ? t('severityError') : t('severityWarning')}
              </Badge>
              <p className='text-sm text-foreground-base whitespace-pre-wrap flex-1'>
                {issue.message}
              </p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
